import React, { useState } from 'react';
import Button from "react-bootstrap/Button";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Table from "react-bootstrap/Table";
import Swal from 'sweetalert2';

import ButtonExit from "../Buttons/ButtonExit";
import { InvoiceFetchAPI } from '../../../api/Invoice';

function BulkInvoicePreview() {
    const name_label = "Menú Principal";
    const token = localStorage.getItem('ushopper-access-token');
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [job, setJob] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [sendEmail, setSendEmail] = useState(false);

    const downloadTemplate = async () => {
        try {
            setIsLoading(true);
            const response = await InvoiceFetchAPI.downloadBulkTemplate(token);
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'template-facturas-bulk.xlsx');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            Swal.fire({
                icon: "error",
                title: "Error",
                text: "No se pudo descargar la plantilla"
            });
        } finally {
            setIsLoading(false);
        }
    };

    const previewInvoices = async (event) => {
        event.preventDefault();

        if (!file) {
            Swal.fire({
                icon: "error",
                title: "Archivo requerido",
                text: "Selecciona un Excel para validar"
            });
            return;
        }

        try {
            setIsLoading(true);
            const formData = new FormData();
            formData.append('files', file);
            const response = await InvoiceFetchAPI.previewBulkInvoices(formData, token);
            setPreview(response.data);
            if (response.data.blocked) {
                Swal.fire({
                    icon: "warning",
                    title: "Revisión requerida",
                    text: `${response.data.warnings.length} advertencias bloqueantes`
                });
            } else {
                Swal.fire({
                    icon: "success",
                    title: "Archivo validado",
                    text: `${response.data.total_invoices} facturas interpretadas`
                });
            }
        } catch (err) {
            const response = err.response && err.response.data;
            setPreview(response || null);
            Swal.fire({
                icon: "error",
                title: "Validación fallida",
                text: response && response.message ? response.message : "No se pudo validar el archivo"
            });
        } finally {
            setIsLoading(false);
        }
    };

    const invoices = preview && preview.invoices ? preview.invoices : [];
    const errors = preview && preview.errors ? preview.errors : [];
    const warnings = preview && preview.warnings ? preview.warnings : [];
    const canGenerate = file && preview && preview.success && !preview.blocked && warnings.length === 0 && errors.length === 0;

    const pollJob = (jobId) => {
        const interval = setInterval(async () => {
            try {
                const response = await InvoiceFetchAPI.getBulkInvoiceJob(jobId, token);
                const nextJob = response.data.job;
                setJob(nextJob);

                if (['completed', 'completed_with_errors', 'failed'].includes(nextJob.status)) {
                    clearInterval(interval);
                }
            } catch (err) {
                clearInterval(interval);
            }
        }, 3000);
    };

    const generateInvoices = async () => {
        if (!canGenerate) return;

        const confirmation = await Swal.fire({
            icon: "warning",
            title: "Generar facturas reales",
            text: sendEmail
                ? "Esto generara facturas reales y enviara correos a los clientes encontrados en BD."
                : "Esto generara facturas reales una por una. No se enviaran correos.",
            input: "text",
            inputPlaceholder: "Escribe GENERAR",
            showCancelButton: true,
            confirmButtonText: "Iniciar cola",
            cancelButtonText: "Cancelar",
            preConfirm: (value) => {
                if (value !== "GENERAR") {
                    Swal.showValidationMessage("Debes escribir GENERAR para continuar");
                }
            }
        });

        if (!confirmation.isConfirmed) return;

        try {
            setIsLoading(true);
            const formData = new FormData();
            formData.append('files', file);
            formData.append('send_email', sendEmail ? 'true' : 'false');
            const response = await InvoiceFetchAPI.generateBulkInvoices(formData, token);
            setJob({
                _id: response.data.job_id,
                status: response.data.status,
                total_invoices: response.data.total_invoices,
                send_email: sendEmail,
                processed: 0,
                generated: 0,
                failed: 0,
                results: []
            });
            pollJob(response.data.job_id);
            Swal.fire({
                icon: "success",
                title: "Cola iniciada",
                text: `${response.data.total_invoices} facturas en cola`
            });
        } catch (err) {
            const response = err.response && err.response.data;
            Swal.fire({
                icon: "error",
                title: "No se pudo iniciar la cola",
                text: response && (response.error || response.message) ? (response.error || response.message) : "Intenta nuevamente"
            });
        } finally {
            setIsLoading(false);
        }
    };

    const resendEmail = async (resultIndex) => {
        if (!job || !job._id) return;

        try {
            setIsLoading(true);
            const response = await InvoiceFetchAPI.resendBulkInvoiceEmail(job._id, resultIndex, token);
            const updatedResult = response.data.result;

            setJob((currentJob) => {
                if (!currentJob) return currentJob;
                const nextResults = [...(currentJob.results || [])];
                nextResults[resultIndex] = updatedResult;
                return { ...currentJob, results: nextResults };
            });

            Swal.fire({
                icon: updatedResult.email_status === 'sent' ? "success" : "warning",
                title: updatedResult.email_status === 'sent' ? "Correo reenviado" : "No se pudo reenviar",
                text: updatedResult.email_error || updatedResult.email_status
            });
        } catch (err) {
            Swal.fire({
                icon: "error",
                title: "Reenvio fallido",
                text: err.response && err.response.data && err.response.data.error
                    ? err.response.data.error
                    : "No se pudo reenviar el correo"
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="mt-5">
            <div className="container">
                <ButtonExit data={name_label} link='/dashboard' />
                <div className="mt-4 d-flex justify-content-center text-ushopper">
                    <h2>Facturas Bulk</h2>
                </div>

                <div className="border rounded shadow-sm p-4 bg-white">
                    <Row className="align-items-end">
                        <Col md={4}>
                            <Button
                                type="button"
                                variant="outline-success"
                                onClick={downloadTemplate}
                                disabled={isLoading}
                            >
                                Descargar plantilla
                            </Button>
                        </Col>
                        <Col md={8}>
                            <Form onSubmit={previewInvoices} encType="multipart/form-data">
                                <Row className="align-items-end">
                                    <Col md={8}>
                                        <Form.Group controlId="bulkInvoiceFile">
                                            <Form.Label>Excel de facturas</Form.Label>
                                            <Form.Control
                                                type="file"
                                                name="files"
                                                accept=".xlsx,.xls"
                                                onChange={(event) => {
                                                    setFile(event.target.files[0]);
                                                    setPreview(null);
                                                    setJob(null);
                                                }}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Button
                                            type="submit"
                                            variant="success"
                                            disabled={isLoading}
                                            className="w-100"
                                        >
                                            Validar
                                        </Button>
                                    </Col>
                                </Row>
                            </Form>
                        </Col>
                    </Row>
                </div>

                {errors.length > 0 && (
                    <div className="border rounded shadow-sm p-4 bg-white mt-4">
                        <h5 className="text-danger">Errores</h5>
                        <Table responsive>
                            <thead>
                                <tr>
                                    <th>Factura</th>
                                    <th>Fila</th>
                                    <th>Detalle</th>
                                </tr>
                            </thead>
                            <tbody>
                                {errors.map((error, index) => (
                                    <tr key={`${error.invoice_ref}-${index}`}>
                                        <td>{error.invoice_ref || '-'}</td>
                                        <td>{error.row || '-'}</td>
                                        <td>{error.errors ? error.errors.join(', ') : '-'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                )}

                {warnings.length > 0 && (
                    <div className="border rounded shadow-sm p-4 bg-white mt-4">
                        <h5 className="text-warning">Advertencias bloqueantes</h5>
                        <Table responsive>
                            <thead>
                                <tr>
                                    <th>Factura</th>
                                    <th>Tipo</th>
                                    <th>Detalle</th>
                                </tr>
                            </thead>
                            <tbody>
                                {warnings.map((warning, index) => (
                                    <tr key={`${warning.invoice_ref}-${warning.type}-${index}`}>
                                        <td>{warning.invoice_ref || '-'}</td>
                                        <td>{warning.type || '-'}</td>
                                        <td>{warning.message || '-'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                )}

                {invoices.length > 0 && (
                    <div className="border rounded shadow-sm p-4 bg-white mt-4">
                        <div className="d-flex justify-content-between align-items-center">
                            <h5>Preview</h5>
                            <div className="d-flex align-items-center gap-3">
                                <span className="text-muted">{invoices.length} facturas</span>
                                <Form.Check
                                    type="switch"
                                    id="bulk-send-email"
                                    label="Enviar correos"
                                    checked={sendEmail}
                                    disabled={isLoading}
                                    onChange={(event) => setSendEmail(event.target.checked)}
                                />
                                <Button
                                    type="button"
                                    variant="danger"
                                    disabled={!canGenerate || isLoading}
                                    onClick={generateInvoices}
                                >
                                    Generar en cola
                                </Button>
                            </div>
                        </div>
                        <Table responsive>
                            <thead>
                                <tr>
                                    <th>Referencia</th>
                                    <th>Cliente</th>
                                    <th>RUC/DV</th>
                                    <th>Dirección</th>
                                    <th>Email</th>
                                    <th>Pago</th>
                                    <th>Items</th>
                                    <th>Producto</th>
                                    <th>Subtotal</th>
                                    <th>ITBMS</th>
                                    <th>Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {invoices.map((invoice) => (
                                    <tr key={invoice.invoice_ref}>
                                        <td>{invoice.invoice_ref}</td>
                                        <td>{invoice.receptor_name}</td>
                                        <td>{invoice.receptor_ruc} / {invoice.receptor_dv}</td>
                                        <td>{invoice.receptor_direction}</td>
                                        <td>{invoice.client_email || '-'}</td>
                                        <td>{invoice.payment_type}</td>
                                        <td>{invoice.items.length}</td>
                                        <td>{invoice.items.map((item) => item.product_id || item.code).join(', ')}</td>
                                        <td>${Number(invoice.subtotal).toFixed(2)}</td>
                                        <td>${Number(invoice.total_itbms).toFixed(2)}</td>
                                        <td>${Number(invoice.total).toFixed(2)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                )}

                {job && (
                    <div className="border rounded shadow-sm p-4 bg-white mt-4">
                        <div className="d-flex justify-content-between align-items-center">
                            <h5>Cola de generacion</h5>
                            <span className="text-muted">{job.status}</span>
                        </div>
                        <Row className="mt-3">
                            <Col md={3}>Procesadas: {job.processed || 0}/{job.total_invoices || 0}</Col>
                            <Col md={3}>Generadas: {job.generated || 0}</Col>
                            <Col md={3}>Fallidas: {job.failed || 0}</Col>
                            <Col md={3}>Actual: {job.current_invoice_ref || '-'}</Col>
                        </Row>
                        {job.results && job.results.length > 0 && (
                            <Table responsive className="mt-3">
                                <thead>
                                    <tr>
                                        <th>Factura</th>
                                        <th>Estado</th>
                                        <th>Orden</th>
                                        <th>CUFE</th>
                                        <th>PDF</th>
                                        <th>Email</th>
                                        <th>Detalle</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {job.results.map((result, index) => (
                                        <tr key={`${result.invoice_ref}-${index}`}>
                                            <td>{result.invoice_ref}</td>
                                            <td>{result.success ? 'Generada' : 'Fallida'}</td>
                                            <td>{result.order_code || '-'}</td>
                                            <td>{result.cufe || '-'}</td>
                                            <td>{result.pdf_url ? <a href={result.pdf_url} target="_blank" rel="noreferrer">PDF</a> : (result.pdf_status || '-')}</td>
                                            <td>
                                                <span>{result.email_status || '-'}</span>
                                                {result.success && result.order_id && (
                                                    <Button
                                                        type="button"
                                                        variant="outline-primary"
                                                        size="sm"
                                                        className="ms-2"
                                                        disabled={isLoading}
                                                        onClick={() => resendEmail(index)}
                                                    >
                                                        Reenviar
                                                    </Button>
                                                )}
                                            </td>
                                            <td>{result.email_error || result.pdf_error || result.xml_error || result.error || result.message || '-'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

export default BulkInvoicePreview;